import { ChangeEvent, Dispatch, SetStateAction, useRef } from "react";

import { type BannerSource } from "@simplyk/common";
import { useSnackbar } from "notistack";
import { FieldValues, Path, PathValue, type UseFormReturn } from "react-hook-form";

import { CroppedFile } from "../../Crop/Crop";
import { ImageSelectionResult } from "../../SelectImageDialog/types";
import { detectImageType, detectMimeTypeFromUrl, getSizeInBite, processFileForUpload } from "../utils";

import { ImageMetaData } from "./useImageMetadata";
import { UpdateFileUrlOptions } from "./useImageUpload";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { LIMIT_10_MB, RECOMMENDED_GIF_MAX_SIZE } from "@/common/constants/image";
import { readFileAsDataURL } from "@/common/helpers/uploadFile";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";

const GIF_MIME_TYPE = "image/gif";

interface UseImageSourceSelectionParams<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  fileUrl: string | undefined | null;
  setFileUrl: Dispatch<SetStateAction<string | undefined | null>>;
  autoFitToSquare: boolean;
  disableCrop: boolean;
  keepGifAnimation: boolean;
  updateFileUrl: (image: string | undefined | null, options?: UpdateFileUrlOptions) => Promise<string | null>;
  openCropEditor: (file: CroppedFile) => void;
  openSquareEditor: (url: string) => void;
  setImageSourceMetadata: Dispatch<SetStateAction<ImageSelectionResult | null>>;
  setSelectImageModalOpen: Dispatch<SetStateAction<boolean>>;
  onBeforeReplace: (() => boolean | Promise<boolean>) | undefined;
  onBeforeDelete: (() => boolean | Promise<boolean>) | undefined;
  onImageSourceChange: ((source: BannerSource | null) => void) | undefined;
  actualImageMetaData: ImageMetaData | undefined;
  fullAmplitudePayload: Record<string, unknown>;
  showImageLibrary: boolean;
  allowZeffyImages: boolean;
  isCustomAssetLibraryLoading: boolean;
  hasMyImages: boolean;
}

export const useImageSourceSelection = <T extends FieldValues>({
  form,
  name,
  fileUrl,
  setFileUrl,
  autoFitToSquare,
  disableCrop,
  keepGifAnimation,
  updateFileUrl,
  openCropEditor,
  openSquareEditor,
  setImageSourceMetadata,
  setSelectImageModalOpen,
  onBeforeReplace,
  onBeforeDelete,
  onImageSourceChange,
  actualImageMetaData,
  fullAmplitudePayload,
  showImageLibrary,
  allowZeffyImages,
  isCustomAssetLibraryLoading,
  hasMyImages,
}: UseImageSourceSelectionParams<T>) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { logAmplitudeEvent } = useAmplitude();

  const { setValue } = form;
  const inputRef = useRef<HTMLInputElement | null>(null);

  const closeSelectImageModalSilently = () => {
    setSelectImageModalOpen(false);
  };

  const shouldUploadGifAsIs = (mimeType: string | undefined) =>
    keepGifAnimation && !autoFitToSquare && mimeType === GIF_MIME_TYPE;

  const uploadGifAsIs = async (image: string, sourceMetadata: ImageSelectionResult | null) => {
    logAmplitudeEvent(AmplitudeEvents.ImageUploaderCropConfirmed, {
      crop_skipped: true,
      ...fullAmplitudePayload,
    });
    closeSelectImageModalSilently();
    await updateFileUrl(image, { sourceMetadata });
  };

  const uploadGifFileAsIs = async (file: File, sourceMetadata: ImageSelectionResult | null) => {
    if (file.size > LIMIT_10_MB) {
      enqueueSnackbar(t("dashboard", "cropDialog.imageSizeLimitOverflow"), { vibe: "danger" });
      return;
    }
    if (file.size > RECOMMENDED_GIF_MAX_SIZE) {
      enqueueSnackbar(t("dashboard", "imageUploader.largeGifWarning"), { vibe: "warning" });
    }
    await uploadGifAsIs(await readFileAsDataURL(file), sourceMetadata);
  };

  const onFileChange = async (file: File) => {
    setImageSourceMetadata(null);

    if (shouldUploadGifAsIs(file.type)) {
      await uploadGifFileAsIs(file, null);
      return;
    }

    const imageDataUrl = await processFileForUpload(file, {
      onHeicError: (file) => {
        enqueueSnackbar(t("common", "heicConversionFailed"), { vibe: "danger" });
        logAmplitudeEvent(AmplitudeEvents.ImageUploaderHEICConversionFailed, {
          file_type: file.type,
          ...fullAmplitudePayload,
        });
      },
      amplitudePayload: fullAmplitudePayload,
    });

    if (autoFitToSquare) {
      openSquareEditor(imageDataUrl);
      closeSelectImageModalSilently();
      return;
    }

    openCropEditor({ url: imageDataUrl, type: file.type });
  };

  const handleImageSelection = async (selection: ImageSelectionResult) => {
    const { url, mimeType } = selection;

    const actualMimeType = mimeType || (await detectMimeTypeFromUrl(url));
    const imageType = actualMimeType || detectImageType(url) || "image/jpeg";

    if (shouldUploadGifAsIs(imageType)) {
      await uploadGifAsIs(url, selection);
      return;
    }

    setImageSourceMetadata(selection);

    if (autoFitToSquare) {
      openSquareEditor(url);
      closeSelectImageModalSilently();
      return;
    }

    openCropEditor({ url, type: imageType });
  };

  const handleNewImageUpload = ({ url, mimeType, file }: { url: string; mimeType: string; file: File }) => {
    if (shouldUploadGifAsIs(mimeType)) {
      void uploadGifFileAsIs(file, { url, mimeType, source: "upload" });
      return;
    }
    void handleImageSelection({ url, mimeType, source: "upload" });
  };

  const handlePickImage = async () => {
    if (fileUrl && onBeforeReplace) {
      const shouldProceed = await onBeforeReplace();
      if (!shouldProceed) {
        return;
      }
    }

    logAmplitudeEvent(AmplitudeEvents.ImageUploaderFlowStarted, {
      entry_point: "Upload from file picker",
      ...fullAmplitudePayload,
    });
    if (inputRef && inputRef.current) {
      inputRef.current.click();
    }
  };

  const onInputFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event && event.target ? event.target.files : null;
    if (files && files[0]) {
      if (disableCrop && files[0].size <= LIMIT_10_MB) {
        const file = files[0];
        const dataUrl = await readFileAsDataURL(file);
        if (getSizeInBite(dataUrl) <= LIMIT_10_MB) {
          logAmplitudeEvent(AmplitudeEvents.ImageUploaderCropConfirmed, {
            crop_skipped: true,
            ...fullAmplitudePayload,
          });
          updateFileUrl(dataUrl);
        }
      } else {
        onFileChange(files[0]);
      }
    }
    event.target.value = "";
  };

  const onDelete = async () => {
    if (onBeforeDelete) {
      const shouldProceed = await onBeforeDelete();
      if (!shouldProceed) {
        return;
      }
    }

    if (inputRef && inputRef.current) {
      inputRef.current.value = "";
    }
    setFileUrl(null);
    setValue(name, null as PathValue<T, Path<T>>, { shouldDirty: true });
    onImageSourceChange?.(null);

    logAmplitudeEvent(AmplitudeEvents.ImageUploaderImageDeleted, {
      original_image_size: actualImageMetaData?.size,
      original_image_width: actualImageMetaData?.width,
      original_image_height: actualImageMetaData?.height,
      original_image_extension: actualImageMetaData?.extension,
      ...fullAmplitudePayload,
    });
  };

  const openSelectImageModal = async () => {
    if (!showImageLibrary) {
      await handlePickImage();
      return;
    }
    if (fileUrl && onBeforeReplace) {
      const shouldProceed = await onBeforeReplace();
      if (!shouldProceed) {
        return;
      }
    }

    const shouldSkipToFilePicker = !allowZeffyImages && !isCustomAssetLibraryLoading && !hasMyImages;

    if (shouldSkipToFilePicker) {
      if (inputRef && inputRef.current) {
        inputRef.current.click();
      }
      return;
    }

    logAmplitudeEvent(AmplitudeEvents.ImageUploaderFlowStarted, {
      entry_point: fileUrl ? "replace_button" : "select_from_library",
      ...fullAmplitudePayload,
    });
    setSelectImageModalOpen(true);
  };

  const closeSelectImageModal = () => {
    logAmplitudeEvent(AmplitudeEvents.ImageUploaderFlowCancelled, {
      cancelled_at: "select_image_dialog",
      ...fullAmplitudePayload,
    });
    setSelectImageModalOpen(false);
  };

  return {
    inputRef,
    onFileChange,
    handleImageSelection,
    handleNewImageUpload,
    handlePickImage,
    onInputFileChange,
    onDelete,
    openSelectImageModal,
    closeSelectImageModal,
  };
};
